/**
 * CanvasCompositor - Merges camera and screen share streams via canvas.
 *
 * Renders screen share as fullscreen with letterboxing/pillarboxing,
 * and camera as a Picture-in-Picture overlay in the bottom-right corner.
 */

type CompositorState = "idle" | "running" | "stopped";

export class CanvasCompositor {
  private canvas: OffscreenCanvas;
  private ctx: OffscreenCanvasRenderingContext2D;
  private outputStream: MediaStream;

  private screenVideo: HTMLVideoElement | null = null;
  private cameraVideo: HTMLVideoElement | null = null;

  private animationFrameId: number | null = null;
  private state: CompositorState = "idle";

  private readonly OUTPUT_WIDTH = 1280;
  private readonly OUTPUT_HEIGHT = 720;
  private readonly FRAME_RATE = 24;

  private readonly PIP_WIDTH_RATIO = 0.25;
  private readonly PIP_PADDING = 16;
  private readonly PIP_BORDER_RADIUS = 8;
  private readonly PIP_BORDER_WIDTH = 2;

  constructor() {
    this.canvas = new OffscreenCanvas(this.OUTPUT_WIDTH, this.OUTPUT_HEIGHT);
    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get 2D context from OffscreenCanvas");
    }
    this.ctx = ctx;

    // Create output stream from canvas
    // Note: captureStream() is not available on OffscreenCanvas in all browsers
    // We need to use a regular canvas for the output stream
    const visibleCanvas = document.createElement("canvas");
    visibleCanvas.width = this.OUTPUT_WIDTH;
    visibleCanvas.height = this.OUTPUT_HEIGHT;
    this.outputStream = visibleCanvas.captureStream(this.FRAME_RATE);

    // Store reference to visible canvas context for copying
    const visibleCtx = visibleCanvas.getContext("2d");
    if (!visibleCtx) {
      throw new Error("Failed to get 2D context from visible canvas");
    }

    // Replace OffscreenCanvas with regular canvas for compatibility
    this.canvas = visibleCanvas as unknown as OffscreenCanvas;
    this.ctx = visibleCtx as unknown as OffscreenCanvasRenderingContext2D;
  }

  /**
   * Start compositing the screen share and camera streams.
   */
  start(screenStream: MediaStream, cameraStream: MediaStream): void {
    if (this.state === "running") {
      console.warn("Compositor already running");
      return;
    }

    this.state = "running";

    // Create video elements for the streams
    this.screenVideo = this.createVideoElement(screenStream);
    this.cameraVideo = this.createVideoElement(cameraStream);

    // Start render loop
    this.renderLoop();
  }

  /**
   * Stop the compositor and clean up resources.
   */
  stop(): void {
    this.state = "stopped";

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.screenVideo) {
      this.screenVideo.srcObject = null;
      this.screenVideo = null;
    }

    if (this.cameraVideo) {
      this.cameraVideo.srcObject = null;
      this.cameraVideo = null;
    }
  }

  /**
   * Get the composited output stream.
   */
  getOutputStream(): MediaStream {
    return this.outputStream;
  }

  /**
   * Get the video track from the output stream.
   */
  getVideoTrack(): MediaStreamTrack | null {
    const tracks = this.outputStream.getVideoTracks();
    return tracks.length > 0 ? tracks[0]! : null;
  }

  private createVideoElement(stream: MediaStream): HTMLVideoElement {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    void video.play();
    return video;
  }

  private renderLoop = (): void => {
    if (this.state !== "running") {
      return;
    }

    this.drawFrame();
    this.animationFrameId = requestAnimationFrame(this.renderLoop);
  };

  private drawFrame(): void {
    const ctx = this.ctx;

    // Clear canvas with black background
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, this.OUTPUT_WIDTH, this.OUTPUT_HEIGHT);

    // Draw screen share - disable smoothing for sharper text/UI
    if (this.screenVideo && this.screenVideo.readyState >= 2) {
      ctx.imageSmoothingEnabled = false;
      this.drawScreenShare();
    }

    // Draw camera PiP - enable high-quality smoothing for downscaled video
    if (this.cameraVideo && this.cameraVideo.readyState >= 2) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      this.drawCameraPiP();
    }
  }

  private drawScreenShare(): void {
    if (!this.screenVideo) return;

    const video = this.screenVideo;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) return;

    // Calculate letterbox/pillarbox dimensions
    const videoAspect = videoWidth / videoHeight;
    const canvasAspect = this.OUTPUT_WIDTH / this.OUTPUT_HEIGHT;

    let drawWidth: number;
    let drawHeight: number;
    let drawX: number;
    let drawY: number;

    if (videoAspect > canvasAspect) {
      // Video is wider - pillarbox (black bars on top/bottom)
      drawWidth = this.OUTPUT_WIDTH;
      drawHeight = this.OUTPUT_WIDTH / videoAspect;
      drawX = 0;
      drawY = (this.OUTPUT_HEIGHT - drawHeight) / 2;
    } else {
      // Video is taller - letterbox (black bars on sides)
      drawHeight = this.OUTPUT_HEIGHT;
      drawWidth = this.OUTPUT_HEIGHT * videoAspect;
      drawX = (this.OUTPUT_WIDTH - drawWidth) / 2;
      drawY = 0;
    }

    this.ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
  }

  private drawCameraPiP(): void {
    if (!this.cameraVideo) return;

    const video = this.cameraVideo;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) return;

    // Calculate PiP dimensions (25% of canvas width)
    const pipWidth = this.OUTPUT_WIDTH * this.PIP_WIDTH_RATIO;
    const videoAspect = videoWidth / videoHeight;
    const pipHeight = pipWidth / videoAspect;

    // Position in bottom-right with padding
    const pipX = this.OUTPUT_WIDTH - pipWidth - this.PIP_PADDING;
    const pipY = this.OUTPUT_HEIGHT - pipHeight - this.PIP_PADDING;

    const ctx = this.ctx;

    // Save context state
    ctx.save();

    // Create rounded rectangle clipping path
    this.roundRect(pipX, pipY, pipWidth, pipHeight, this.PIP_BORDER_RADIUS);
    ctx.clip();

    // Draw mirrored camera (horizontal flip for natural self-view)
    ctx.translate(pipX + pipWidth, pipY);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, pipWidth, pipHeight);

    // Restore context state
    ctx.restore();

    // Draw subtle white border
    ctx.save();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
    ctx.lineWidth = this.PIP_BORDER_WIDTH;
    this.roundRect(pipX, pipY, pipWidth, pipHeight, this.PIP_BORDER_RADIUS);
    ctx.stroke();
    ctx.restore();
  }

  private roundRect(x: number, y: number, width: number, height: number, radius: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }
}
